"use client";

import Button from "@/components/ui/Button/Button";
import { getVimeoId } from "@/helpers/vimeoId";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Vimeo from "@u-wave/react-vimeo";

export default function CourseVideoHeader(props) {
    const router = useRouter();
    let highlightedVideoRef = useRef(null);
    const observerRef = useRef(null);

    const [updating, setUpdating] = useState(false);
    const [video, setVideo] = useState(null);
    const [videoIsPlaying, setVideoIsPlaying] = useState(true);
    const [videoIsMuted, setVideoIsMuted] = useState(true);
    const [videoIsForcedPaused, setVideoIsForcedPaused] = useState(false);
    const [videoIsLoading, setVideoIsLoading] = useState(false);
    const [videoEnd, setVideoEnd] = useState(false);
    const [refreshVideo, setRefreshVideo] = useState(false);

    // Callbacks
    const restartVideo = useCallback(() => {
        video.setCurrentTime(0);
    }, [video]);

    let controls = (
        <>
            {/* Mute */}
            <div
                className="rounded-full bg-white bg-opacity-50 p-3 cursor-pointer hover:bg-opacity-100 group duration-150"
                onClick={() => setVideoIsMuted(!videoIsMuted)}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 group-hover:text-primary duration-150"
                    viewBox="0 0 24 24"
                >
                    {videoIsMuted ? (
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            d="M12.14 4.91c-.714.347-1.62.942-2.909 1.792l-.219.144l-.05.033c-.38.25-.643.424-.931.552a3.75 3.75 0 0 1-.89.267c-.31.052-.626.052-1.082.052H6c-1.444 0-1.93.016-2.345.208a2.47 2.47 0 0 0-1.036.975c-.218.405-.259.82-.336 2.084c-.02.347-.033.68-.033.983c0 .303.012.636.033.983c.077 1.264.118 1.68.336 2.084c.209.389.635.79 1.036.975c.414.192.901.208 2.345.208h.06c.455 0 .77 0 1.081.052c.307.052.605.142.89.267c.288.128.552.301.932.552l.05.033l.218.144c1.29.85 2.195 1.445 2.91 1.792c.711.346 1.068.36 1.317.275c.137-.047.272-.115.39-.199c.217-.152.415-.447.553-1.218c.14-.775.191-1.847.263-3.377c.052-1.12.086-2.06.086-2.571c0-.512-.034-1.451-.086-2.57c-.072-1.531-.123-2.603-.262-3.378c-.14-.771-.337-1.066-.553-1.218a1.569 1.569 0 0 0-.391-.2c-.25-.084-.606-.07-1.318.276Zm-.655-1.35c.826-.4 1.64-.624 2.457-.345c.272.093.534.226.769.391c.706.497 1.005 1.28 1.167 2.18c.159.884.213 2.056.281 3.516l.003.058c.052 1.115.088 2.088.088 2.64s-.036 1.525-.088 2.64l-.003.058c-.068 1.46-.122 2.632-.281 3.516c-.162.9-.461 1.683-1.167 2.18a3.066 3.066 0 0 1-.769.39c-.818.28-1.631.057-2.457-.345c-.814-.395-1.8-1.046-3.032-1.857l-.267-.176c-.447-.295-.602-.394-.76-.464a2.17 2.17 0 0 0-.535-.16c-.171-.03-.354-.032-.89-.032h-.162c-1.217 0-2.062.001-2.814-.347a3.962 3.962 0 0 1-1.727-1.624c-.392-.729-.438-1.49-.504-2.575l-.008-.13A17.876 17.876 0 0 1 .75 12c0-.341.014-.706.036-1.074l.008-.13C.86 9.712.906 8.95 1.298 8.22a3.962 3.962 0 0 1 1.727-1.624c.752-.348 1.597-.348 2.814-.347H6c.537 0 .72-.002.891-.031a2.25 2.25 0 0 0 .534-.16c.16-.07.314-.17.761-.465l.267-.176c1.231-.811 2.218-1.462 3.032-1.857Zm8.035 1.864a.75.75 0 0 1 1.056.096L20 6l.576-.48l.001.001l.002.002l.003.004l.007.009a2.395 2.395 0 0 1 .086.114a4.7 4.7 0 0 1 .203.311c.161.27.368.665.572 1.195c.408 1.061.8 2.653.8 4.844c0 2.192-.392 3.783-.8 4.845a7.7 7.7 0 0 1-.572 1.194a4.677 4.677 0 0 1-.268.399l-.013.017l-.008.01l-.007.009l-.003.003l-.002.002L20 18l.576.48a.75.75 0 0 1-1.156-.955m0 0l.003-.005l.031-.041a3.28 3.28 0 0 0 .137-.212c.12-.198.288-.516.459-.961c.342-.889.7-2.297.7-4.306c0-2.008-.358-3.417-.7-4.306a6.212 6.212 0 0 0-.459-.961a3.274 3.274 0 0 0-.171-.257l.003.004l-.001-.002l-.002-.002a.75.75 0 0 1 .1-1.052m-1.884 2.92a.75.75 0 0 1 1.02.292l-.628.349l.628-.349v.001l.001.002l.002.003l.004.007l.01.018l.026.054a5.001 5.001 0 0 1 .278.805c.14.551.273 1.364.273 2.474s-.132 1.923-.273 2.474a5.172 5.172 0 0 1-.203.631a2.947 2.947 0 0 1-.102.228l-.01.019l-.003.007l-.002.003v.001s-.001.002-.63-.347l.629.348a.75.75 0 0 1-1.317-.719l.005-.01a3.665 3.665 0 0 0 .18-.534c.108-.424.226-1.11.226-2.1s-.118-1.678-.227-2.102a3.662 3.662 0 0 0-.179-.534l-.005-.01a.75.75 0 0 1 .297-1.01Z"
                            clipRule="evenodd"
                        ></path>
                    ) : (
                        <path
                            fill="currentColor"
                            fillRule="evenodd"
                            d="M22.53 1.47a.75.75 0 0 1 0 1.06l-20 20a.75.75 0 0 1-1.06-1.06l3.721-3.722c-.883-.01-1.555-.062-2.166-.345a3.962 3.962 0 0 1-1.727-1.624c-.392-.729-.438-1.49-.504-2.575l-.008-.13A17.876 17.876 0 0 1 .75 12c0-.341.014-.706.036-1.074l.008-.13C.86 9.712.906 8.95 1.298 8.22a3.962 3.962 0 0 1 1.727-1.624c.752-.348 1.597-.348 2.814-.347H6c.537 0 .72-.002.891-.031a2.25 2.25 0 0 0 .534-.16c.16-.07.314-.17.761-.465l.267-.176c1.231-.811 2.218-1.462 3.032-1.857c.826-.402 1.64-.625 2.457-.346c.272.093.534.226.769.391c.64.45.944 1.135 1.116 1.924c.088.402.148.865.193 1.389l5.45-5.45a.75.75 0 0 1 1.06 0Zm-7.92 6.859c-.057-1.101-.121-1.894-.249-2.48c-.136-.624-.317-.878-.513-1.015a1.569 1.569 0 0 0-.39-.2c-.25-.084-.606-.07-1.318.276c-.714.347-1.62.942-2.909 1.792l-.219.144l-.05.033c-.38.25-.643.424-.931.552a3.75 3.75 0 0 1-.89.267c-.31.052-.626.052-1.082.052H6c-1.444 0-1.93.016-2.345.208a2.47 2.47 0 0 0-1.036.975c-.218.405-.259.82-.336 2.084c-.02.347-.033.68-.033.983c0 .303.012.636.033.983c.077 1.264.118 1.68.336 2.084c.209.389.635.79 1.036.975c.414.192.901.208 2.345.208h.06c.239 0 .44 0 .622.008l7.928-7.93Zm5.655-2.013a.75.75 0 0 1 .991.376c.468 1.035.994 2.768.994 5.308c0 2.192-.392 3.783-.8 4.844a7.72 7.72 0 0 1-.572 1.195a4.73 4.73 0 0 1-.289.425l-.007.01l-.003.003l-.002.002L20 18l.576.48a.75.75 0 0 1-1.156-.956l.003-.004l.031-.041a3.27 3.27 0 0 0 .137-.212c.12-.199.288-.516.459-.961c.342-.889.7-2.298.7-4.306c0-2.326-.48-3.849-.86-4.692a.75.75 0 0 1 .375-.992Zm-2.101 2.95a.75.75 0 0 1 .887.582c.11.53.199 1.24.199 2.152c0 1.11-.132 1.923-.273 2.474a5.172 5.172 0 0 1-.203.631a2.947 2.947 0 0 1-.102.228l-.01.018l-.003.007l-.002.003v.002s-.001.001-.63-.348l.629.35a.75.75 0 0 1-1.317-.72l.005-.01a1.85 1.85 0 0 0 .038-.087a3.66 3.66 0 0 0 .141-.447c.11-.424.227-1.111.227-2.101a9.17 9.17 0 0 0-.168-1.848a.75.75 0 0 1 .582-.886ZM15.5 11.25a.75.75 0 0 1 .75.75c0 .552-.036 1.525-.088 2.64l-.003.058c-.068 1.46-.122 2.632-.281 3.516c-.162.9-.461 1.683-1.167 2.18a3.066 3.066 0 0 1-.769.39c-.742.254-1.48.093-2.222-.235c-.732-.324-1.592-.868-2.631-1.549a.75.75 0 1 1 .822-1.255c1.059.694 1.815 1.166 2.416 1.432c.592.262.905.265 1.13.188c.138-.047.273-.115.391-.199c.217-.152.415-.447.553-1.218c.14-.775.191-1.847.263-3.377c.052-1.12.086-2.06.086-2.571a.75.75 0 0 1 .75-.75Z"
                            clipRule="evenodd"
                        ></path>
                    )}
                </svg>
            </div>

            {/* Pause */}
            <div
                className="rounded-full bg-white bg-opacity-50 p-3 cursor-pointer hover:bg-opacity-100 group duration-150"
                onClick={() => {
                    if (!videoIsPlaying) {
                        // highlightedVideoRef.current.play();
                    } else {
                        // highlightedVideoRef.current.pause();
                    }
                    setVideoIsForcedPaused(!videoIsForcedPaused);
                    setVideoIsPlaying(!videoIsPlaying);
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 group-hover:text-primary duration-150"
                    viewBox="0 0 24 24"
                >
                    {!videoIsPlaying ? (
                        <path
                            fill="currentColor"
                            d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                        ></path>
                    ) : (
                        <path
                            fill="currentColor"
                            d="M2 6c0-1.886 0-2.828.586-3.414C3.172 2 4.114 2 6 2c1.886 0 2.828 0 3.414.586C10 3.172 10 4.114 10 6v12c0 1.886 0 2.828-.586 3.414C8.828 22 7.886 22 6 22c-1.886 0-2.828 0-3.414-.586C2 20.828 2 19.886 2 18V6Zm12 0c0-1.886 0-2.828.586-3.414C15.172 2 16.114 2 18 2c1.886 0 2.828 0 3.414.586C22 3.172 22 4.114 22 6v12c0 1.886 0 2.828-.586 3.414C20.828 22 19.886 22 18 22c-1.886 0-2.828 0-3.414-.586C14 20.828 14 19.886 14 18V6Z"
                        ></path>
                    )}
                </svg>
            </div>

            {/* Reload the video */}
            <div
                className="rounded-full bg-white bg-opacity-50 p-3 cursor-pointer hover:bg-opacity-100 group duration-150"
                onClick={() => {
                    restartVideo();
                    setVideoIsPlaying(true);
                    setVideoIsForcedPaused(false);
                    setVideoIsMuted(false);
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 group-hover:text-primary duration-150"
                    viewBox="0 0 24 24"
                >
                    <path
                        fill="currentColor"
                        fillRule="evenodd"
                        d="M2.93 11.2c.072-4.96 4.146-8.95 9.149-8.95a9.158 9.158 0 0 1 7.814 4.357a.75.75 0 0 1-1.277.786a7.658 7.658 0 0 0-6.537-3.643c-4.185 0-7.575 3.328-7.648 7.448l.4-.397a.75.75 0 0 1 1.057 1.065l-1.68 1.666a.75.75 0 0 1-1.056 0l-1.68-1.666A.75.75 0 1 1 2.528 10.8l.403.4Zm16.856-.733a.75.75 0 0 1 1.055 0l1.686 1.666a.75.75 0 1 1-1.054 1.067l-.41-.405c-.07 4.965-4.161 8.955-9.18 8.955a9.197 9.197 0 0 1-7.842-4.356a.75.75 0 1 1 1.277-.788a7.697 7.697 0 0 0 6.565 3.644c4.206 0 7.61-3.333 7.68-7.453l-.408.403a.75.75 0 1 1-1.055-1.067l1.686-1.666Z"
                        clipRule="evenodd"
                    ></path>
                </svg>
            </div>
        </>
    );

    // Methods
    const buyCourse = async () => {
        if (!updating) {
            if (!props.session) {
                toast.success(
                    "Avant de rejoindre cette formation il vous faut un compte Believemy.",
                );
                router.push(
                    `/signup?courseImage=${props.course.image}&courseSlug=${props.course.slug}`,
                );
                return;
            }

            setUpdating(true);

            let response = await fetch("/api/course/post/buy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    slug: props.course.slug,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setUpdating(false);
                toast.error(data.message);
                if (data.already) {
                    router.push(`/course/${props.course.slug}/1/1?start=1`);
                } else {
                    router.push("/pricing");
                }
                return;
            } else {
                setUpdating(false);
                toast.success("Bienvenue sur votre formation Believemy");
                router.push(`/course/${props.course.slug}/1/1?start=1`);
                return;
            }
        }
    };

    const loaded = () => {
        setVideoIsLoading(false);
    };

    // Lifecycle
    useEffect(() => {
        let observer = false;
        const videoElement = highlightedVideoRef.current;
        const options = {
            rootMargin: "0px",
            threshold: 0.4, // 10% de la vidéo visible
        };

        if (videoElement != null) {
            const handleIntersection = (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (!videoIsForcedPaused) {
                            // La vidéo est visible
                            // videoElement.play();
                            setVideoIsPlaying(true);
                        }
                    } else {
                        // La vidéo n'est plus visible
                        // videoElement.pause();
                        setVideoIsPlaying(false);
                    }
                });
            };

            observer = new IntersectionObserver(handleIntersection, options);

            observer.observe(videoElement.player.element);

            observerRef.current = observer;

            return () => {
                if (observerRef && observerRef.current) {
                    observerRef.current.unobserve(videoElement.player.element);
                }
            };
        }
    }, [highlightedVideoRef, videoIsForcedPaused, refreshVideo]);

    useEffect(() => {
        // Timer 3sec
        const timer = setTimeout(() => {
            setRefreshVideo(!refreshVideo);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            <div className="lg:m-auto mx-5 lg:rounded-none rounded-xl overflow-hidden relative">
                <div className="lg:hidden block h-24"></div>
                <div className="aspect-w-16 aspect-h-9 relative w-full h-0 bg-black z-0 lg:rounded-t-none rounded-t-xl overflow-hidden">
                    <div
                        className="flex flex-col z-10 w-full top-0 left-0 bottom-0 right-0 absolute transition-all duration-500"
                        style={{
                            background:
                                // (!highlightEnd && !videoIsLoading) ||
                                // (highlight && !highlightEnd) ||
                                // !highlight
                                props.course.video &&
                                !videoEnd &&
                                !videoIsLoading
                                    ? "transparent"
                                    : `url('${
                                          props.course.storyArt
                                              ? props.course.storyArt
                                              : props.course.socialImage
                                      }') lightgray 50% / cover no-repeat`,
                        }}
                    ></div>
                    <Vimeo
                        video={getVimeoId(props.course.video)}
                        showByline={false}
                        color="8468e3"
                        showTitle={false}
                        showPortrait={false}
                        background={true}
                        speed
                        className="z-0 object-cover left-0 top-0 bottom-0 right-0 absolute"
                        dnt
                        responsive
                        ref={highlightedVideoRef}
                        muted={videoIsMuted}
                        onEnd={() => {
                            setVideoEnd(true);
                            setVideoIsPlaying(false);
                        }}
                        paused={!videoIsPlaying}
                        key={`video-${props.course.video}`}
                        autoplay
                        volume={videoIsMuted ? 0 : 1}
                        quality="1080p"
                        onReady={setVideo}
                        onLoaded={loaded}
                    />
                    <div className="bg-for-article dark:bg-for-articleDark">
                        <div
                            className="bottom-0 left-0 opacity-75 absolute top-0 z-0"
                            style={{
                                background:
                                    "linear-gradient(77deg,rgba(0,0,0,.6),transparent 85%)",
                                right: "10.09%",
                            }}
                        ></div>
                        {/* Main title */}
                        {props.course && (
                            <div
                                className="flex-1 flex flex-col justify-end text-white z-30 absolute"
                                style={{
                                    left: "4%",
                                    bottom: "30%",
                                    top: "0",
                                    width: "36%",
                                }}
                            >
                                <div className="z-30 lg:block hidden">
                                    {props.course && props.course.imageTitle ? (
                                        <Image
                                            src={props.course.imageTitle}
                                            alt="Logo"
                                            width={625 * 0.8}
                                            height={250 * 0.8}
                                            className="object-contain"
                                        />
                                    ) : (
                                        <h1
                                            className="bold"
                                            style={{
                                                marginBottom: "1.2vw",
                                                textShadow:
                                                    "2px 2px 4px rgba(0, 0, 0, .45)",
                                                fontSize: "2.5vw",
                                            }}
                                        >
                                            {props.course.name}
                                        </h1>
                                    )}
                                    <div className="text-lg mb-3 opacity-80">
                                        {props.course.isBelievemyOriginals &&
                                            "Believemy Originals・"}
                                        {props.course.duration}・
                                        {props.course.productCategory ==
                                        "entrepreneurs"
                                            ? "Entrepreneuriat"
                                            : "Développement"}
                                        ・Formation
                                        {props.course.subtitles && (
                                            <div
                                                className="inline-flex items-center"
                                                title="Sous-titres disponibles"
                                            >
                                                ・
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-7 h-7"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        fillRule="evenodd"
                                                        d="M2 12c0-3.771 0-5.657 1.172-6.828C4.343 4 6.229 4 10 4h4c3.771 0 5.657 0 6.828 1.172C22 6.343 22 8.229 22 12c0 3.771 0 5.657-1.172 6.828C19.657 20 17.771 20 14 20h-4c-3.771 0-5.657 0-6.828-1.172C2 17.657 2 15.771 2 12Zm4 3.25a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5H6ZM7.75 13a.75.75 0 0 0-.75-.75H6a.75.75 0 0 0 0 1.5h1a.75.75 0 0 0 .75-.75Zm3.75-.75a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1 0-1.5h2Zm7.25.75a.75.75 0 0 0-.75-.75h-4a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 .75-.75Zm-6.25 2.25a.75.75 0 0 0 0 1.5H14a.75.75 0 0 0 0-1.5h-1.5Zm3.25.75a.75.75 0 0 1 .75-.75H18a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </svg>
                                            </div>
                                        )}
                                        {props.course.has4KResolution && (
                                            <div
                                                className="inline-flex items-center"
                                                title="Résolution en 4K disponible"
                                            >
                                                ・
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-5 h-5"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        fillRule="evenodd"
                                                        d="M2 12c0-4.714 0-7.071 1.464-8.536C4.93 2 7.286 2 12 2c4.714 0 7.071 0 8.535 1.464C22 4.93 22 7.286 22 12c0 4.714 0 7.071-1.465 8.535C19.072 22 16.714 22 12 22s-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12Zm4.25-4a.75.75 0 0 0-1.5 0v2a2.75 2.75 0 0 0 2.75 2.75h2.25V16a.75.75 0 0 0 1.5 0V8a.75.75 0 0 0-1.5 0v3.25H7.5c-.69 0-1.25-.56-1.25-1.25V8Zm12.77-.54a.75.75 0 0 1 .02 1.06l-2.666 2.773l2.757 4.302a.75.75 0 1 1-1.262.81l-2.564-4l-1.055 1.097V16a.75.75 0 0 1-1.5 0V8a.75.75 0 0 1 1.5 0v3.338l3.71-3.858a.75.75 0 0 1 1.06-.02Z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        className="lg:w-144 w-full"
                                        style={{
                                            lineHeight: "135%",
                                            textShadow:
                                                "2px 2px 4px rgba(0, 0, 0, .45)",
                                            fontSize: "1.2vw",
                                        }}
                                    >
                                        {props.course.summary}
                                    </div>
                                    <div className="flex items-center gap-4 mt-5 z-30">
                                        <Button
                                            type="white"
                                            onClick={buyCourse}
                                            isLoading={updating}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 mr-3 text-excitationOrange"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                                                ></path>
                                            </svg>
                                            Commencer
                                        </Button>
                                        <div className="inline-flex items-center gap-3">
                                            {controls}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="lg:hidden flex justify-center dark:bg-bgDark-840 bg-gray-100">
                    <div className="inline-block transform -translate-y-1/2">
                        <Button
                            type="white"
                            onClick={buyCourse}
                            isLoading={updating}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 mr-3 text-excitationOrange"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="currentColor"
                                    d="M21.409 9.353a2.998 2.998 0 0 1 0 5.294L8.597 21.614C6.534 22.736 4 21.276 4 18.968V5.033c0-2.31 2.534-3.769 4.597-2.648l12.812 6.968Z"
                                ></path>
                            </svg>
                            Commencer
                        </Button>
                    </div>
                </div>

                <div className="lg:hidden bg-gray-100 dark:bg-bgDark-840 rounded-b-lg p-3">
                    {props.course && props.course.imageTitle ? (
                        <Image
                            src={props.course.imageTitle}
                            alt="Logo"
                            width={625 * 0.8}
                            height={250 * 0.8}
                            className="object-contain"
                        />
                    ) : (
                        <div className="bold dark:text-white text-xl text-center -mt-5">
                            {props.course.name}
                        </div>
                    )}
                    <div className="mt-3 dark:text-white text-center opacity-50">
                        {props.course.isBelievemyOriginals &&
                            "Believemy Originals・"}
                        {props.course.duration}・
                        {props.course.productCategory == "entrepreneurs"
                            ? "Entrepreneuriat"
                            : "Développement"}
                        ・Formation
                        {props.course.subtitles && (
                            <div
                                className="inline-flex items-center"
                                title="Sous-titres disponibles"
                            >
                                ・
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-7 h-7"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M2 12c0-3.771 0-5.657 1.172-6.828C4.343 4 6.229 4 10 4h4c3.771 0 5.657 0 6.828 1.172C22 6.343 22 8.229 22 12c0 3.771 0 5.657-1.172 6.828C19.657 20 17.771 20 14 20h-4c-3.771 0-5.657 0-6.828-1.172C2 17.657 2 15.771 2 12Zm4 3.25a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5H6ZM7.75 13a.75.75 0 0 0-.75-.75H6a.75.75 0 0 0 0 1.5h1a.75.75 0 0 0 .75-.75Zm3.75-.75a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1 0-1.5h2Zm7.25.75a.75.75 0 0 0-.75-.75h-4a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 .75-.75Zm-6.25 2.25a.75.75 0 0 0 0 1.5H14a.75.75 0 0 0 0-1.5h-1.5Zm3.25.75a.75.75 0 0 1 .75-.75H18a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Z"
                                        clipRule="evenodd"
                                    ></path>
                                </svg>
                            </div>
                        )}
                        {props.course.has4KResolution && (
                            <div
                                className="inline-flex items-center"
                                title="Résolution en 4K disponible"
                            >
                                ・
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-5 h-5"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M2 12c0-4.714 0-7.071 1.464-8.536C4.93 2 7.286 2 12 2c4.714 0 7.071 0 8.535 1.464C22 4.93 22 7.286 22 12c0 4.714 0 7.071-1.465 8.535C19.072 22 16.714 22 12 22s-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12Zm4.25-4a.75.75 0 0 0-1.5 0v2a2.75 2.75 0 0 0 2.75 2.75h2.25V16a.75.75 0 0 0 1.5 0V8a.75.75 0 0 0-1.5 0v3.25H7.5c-.69 0-1.25-.56-1.25-1.25V8Zm12.77-.54a.75.75 0 0 1 .02 1.06l-2.666 2.773l2.757 4.302a.75.75 0 1 1-1.262.81l-2.564-4l-1.055 1.097V16a.75.75 0 0 1-1.5 0V8a.75.75 0 0 1 1.5 0v3.338l3.71-3.858a.75.75 0 0 1 1.06-.02Z"
                                        clipRule="evenodd"
                                    ></path>
                                </svg>
                            </div>
                        )}
                    </div>
                    <div className="mt-3 dark:text-lightBlue text-gray-600 text-center">
                        {props.course.summary}
                    </div>
                    <div className="flex items-center gap-3 justify-center mt-3 text-black dark:text-white">
                        {controls}
                    </div>
                </div>
            </div>
        </>
    );
}
